import React, { useState, useEffect, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import CustomTooltip from '../../../Tooltip';
import PreviewOriginal from '../../../PreviewOriginal';
import ConstructorBanners from '../../../constructors/Banners/ConstructorBanners.container';
import BannerPreview from '../../../BannerPreview';
import DataComponentPromo from "../../../DataComponent/DataComponentPromo";
import {cap} from "../../../../config";
import { I18nContext } from 'utils/I18n/context';

const columnTableData = {
    header: {
        0: {className: 'text-center', style: {width: 50}},
        4: {style: {width: 100}}
    },
    body: {
        0: {className: 'text-center'}
    }
};

const OfferPagePromoBanners = ({ 
    locales, 
    dimensions, 
    mimeTypes, 
    proportions, 
    headerNames, 
    banners, 
    loading, 
    totalPages, 
    getLocales, 
    getDimensions, 
    getMimeTypes, 
    getBanners: getBannersAction, 
    saveBannerPresets 
}) => {
    const { t } = useContext(I18nContext);
    const { id } = useParams();
    const [state, setState] = useState({
        offerId: id,
        banners: [],
        locales: locales,
        dimensions: dimensions,
        mimeTypes: mimeTypes,
        proportions: proportions,
        headerNames: headerNames,
        loading: loading,
        totalPages: totalPages,
        modalIsVisible: false,
        constructorIsVisible: false,
        selectedBanner: {}
    });

    useEffect(() => {
        const offerId = state.offerId;
        if (!offerId) return;
        const payload = { offerId };
        getLocales(payload);
        getDimensions(payload);
        getMimeTypes(payload);
    }, [getLocales, getDimensions, getMimeTypes, state.offerId]);

    useEffect(() => {
        setState(prev => ({
            ...prev,
            offerId: id,
            loading: loading,
            banners: Array.isArray(banners) ? banners : [],
            locales: Array.isArray(locales) ? locales : [],
            dimensions: Array.isArray(dimensions) ? dimensions : [],
            mimeTypes: Array.isArray(mimeTypes) ? mimeTypes : [],
            totalPages: Number(totalPages) || 0
        }));
    }, [id, loading, banners, locales, dimensions, mimeTypes, totalPages]);

    const showModalPreview = useCallback((data) => {
        setState(prev => ({
            ...prev,
            modalIsVisible: true,
            selectedBanner: data
        }));
    }, []);

    const showConstructor = useCallback((data) => {
        setState(prev => ({
            ...prev,
            constructorIsVisible: true,
            selectedBanner: data
        }));
    }, []);

    const closeModal = useCallback(() => {
        setState(prev => ({
            ...prev,
            modalIsVisible: false
        }));
    }, []);

    const closeConstructor = useCallback(() => {
        setState(prev => ({
            ...prev,
            constructorIsVisible: false
        }));
    }, []);

    const onClickShowModalPreview = useCallback((event) => {
        const index = event.target.closest('.control-item').getAttribute('data-index');
        showModalPreview(state.banners[index]);
    }, [state.banners, showModalPreview]);

    const onClickShowConstructor = useCallback((index) => {
        showConstructor(state.banners[index]);
    }, [state.banners, showConstructor]);

    const getBanners = useCallback(() => {
        let banners = [];

        state.banners.map((banner, i) => {
            const mimeTypes = state.mimeTypes.find((item) => item.value.indexOf(banner.mime_type) !== -1);

            const dataPreview = {
                url: banner.url,
                originalWidth: banner.width,
                originalHeight: banner.height
            };

            let itemTitle = banner.title.slice(0,60);
            if (itemTitle.length < banner.title.length) {
                itemTitle += ' ...';
            }

            banners.push({
                id: banner.id,
                title: (
                    <CustomTooltip id={`banner-view-title-${i}`} tooltip={<PreviewOriginal {...dataPreview} />} placement={'right'} theme={'preview'}>
                        <div className="table-title" id={`banner-view-title-${i}`}>{itemTitle}</div>
                    </CustomTooltip>
                ),
                image: (
                    <div className="offer-item__table-img">
                        {banner.mime_type
                            ? (
                                <CustomTooltip id={`link-preview-${i}`} tooltip={<PreviewOriginal {...dataPreview} />} placement={'right'} theme={'preview'}>
                                    <a href={banner.url} id={`link-preview-${i}`} target="_blank">
                                        <img src={banner.url || cap} alt="" />
                                    </a>
                                </CustomTooltip>
                            )
                            : (
                                <a href={banner.url} target="_blank">
                                    <img src={cap} alt="" />
                                </a>
                            )
                        }
                    </div>
                ),
                url: banner.dimensions,
                action: null
            });
        });

        return banners;
    }, [state.banners, state.mimeTypes]);

    const onDataTableAction = useCallback((dataComponentState, actionType) => {
        getBannersAction({
            ...dataComponentState,
            offerId: state.offerId
        }, actionType);
    }, [getBannersAction, state.offerId]);

    return (
        <React.Fragment>
            <DataComponentPromo
                onAction={onDataTableAction}
                totalPages={state.totalPages}
                headerNames={state.headerNames}
                data={getBanners()}
                loading={state.loading}
                withoutDatepicker={true}
                textInput={true}
                alignPagination={true}
                columnData={columnTableData}
                hidden={true}
            />
            <BannerPreview
                isVisible={state.modalIsVisible}
                url={state.selectedBanner.url}
                title={state.selectedBanner.title}
                width={state.selectedBanner.width}
                height={state.selectedBanner.height}
                onClose={closeModal}
            />
            <ConstructorBanners
                visible={state.constructorIsVisible}
                banner={state.selectedBanner}
                onClose={closeConstructor}
                onSave={saveBannerPresets}
            />
        </React.Fragment>
    );
};

OfferPagePromoBanners.propTypes = {
    locales: PropTypes.array,
    dimensions: PropTypes.array,
    mimeTypes: PropTypes.array,
    proportions: PropTypes.array,
    headerNames: PropTypes.array,
    loading: PropTypes.bool,
    totalPages: PropTypes.number,
    getLocales: PropTypes.func,
    getDimensions: PropTypes.func,
    getMimeTypes: PropTypes.func,
    getBanners: PropTypes.func,
    saveBannerPresets: PropTypes.func
};

OfferPagePromoBanners.defaultProps = {
    locales: [],
    dimensions: [],
    mimeTypes: [],
    proportions: [],
    headerNames: [],
    loading: false,
    totalPages: 0,
    getLocales: () => {},
    getDimensions: () => {},
    getMimeTypes: () => {},
    getBanners: () => {},
    saveBannerPresets: () => {}
};

export { OfferPagePromoBanners };
